// Random-restart backtracking generator. Fills the grid left-to-right,
// top-to-bottom, trying the digits 1..9 in a per-call shuffled order so the
// emitted board is fully randomized rather than biased toward the canonical
// solution. The classic 9×9 sudoku has so many completions that pure
// backtracking terminates well within budget — no constraint propagation
// needed at this stage.

import {
  BOARD_SIZE,
  BOX_SIZE,
  CELL_COUNT,
  DIGITS,
  emptyGrid,
  type Digit,
  type Grid,
  type NonEmptyDigit,
} from '../game/types.js';
import { mulberry32, shuffleInPlace, type Rng } from '../utils/prng.js';

const ALL_DIGITS_MASK = 0b1111111110;

function bit(digit: NonEmptyDigit): number {
  return 1 << digit;
}

/**
 * Generate a complete, valid 9×9 sudoku solution. Deterministic in `seed`.
 *
 * The same `seed` always yields the same grid — this is the load-bearing
 * property the carver + URL share flow rely on.
 */
export function generateSolution(seed: number): Grid {
  const rng = mulberry32(seed);
  const grid = emptyGrid();
  const rowMask = new Array<number>(BOARD_SIZE).fill(0);
  const colMask = new Array<number>(BOARD_SIZE).fill(0);
  const boxMask = new Array<number>(BOARD_SIZE).fill(0);
  if (!fill(grid, 0, rowMask, colMask, boxMask, rng)) {
    throw new Error('Solution generation failed — backtracking exhausted with no completion');
  }
  return grid;
}

function fill(
  grid: Grid,
  index: number,
  rowMask: number[],
  colMask: number[],
  boxMask: number[],
  rng: Rng,
): boolean {
  if (index === CELL_COUNT) return true;
  const row = Math.floor(index / BOARD_SIZE);
  const col = index % BOARD_SIZE;
  const box = Math.floor(row / BOX_SIZE) * BOX_SIZE + Math.floor(col / BOX_SIZE);
  const rm = rowMask[row] ?? 0;
  const cm = colMask[col] ?? 0;
  const bm = boxMask[box] ?? 0;
  const used = rm | cm | bm;
  if ((used & ALL_DIGITS_MASK) === ALL_DIGITS_MASK) return false;

  const order = shuffleInPlace(rng, DIGITS.slice() as NonEmptyDigit[]);
  for (const digit of order) {
    const b = bit(digit);
    if ((used & b) !== 0) continue;
    grid[index] = digit as Digit;
    rowMask[row] = rm | b;
    colMask[col] = cm | b;
    boxMask[box] = bm | b;
    if (fill(grid, index + 1, rowMask, colMask, boxMask, rng)) return true;
    grid[index] = 0;
    rowMask[row] = rm;
    colMask[col] = cm;
    boxMask[box] = bm;
  }
  return false;
}
